import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calculator, Plus, Pencil, Trash2, MapPin, Loader2, Power, User, KeyRound, Copy, RefreshCw, Mail, MessageCircle, History, ShieldCheck, FileSpreadsheet, FileText } from "lucide-react";
import { curSym } from "@/lib/company";

const emptyForm = { name: "", location_id: "", active: true, personnel_id: "", open_password: "" };
const fmtDT = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Registers() {
  const { user: me, can } = useAuth();
  const canManage = can("register.manage") || can("user.assign") || can("user.manage") || can("location.manage");
  const [regs, setRegs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [regenerate, setRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locFilter, setLocFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [creds, setCreds] = useState(null); // { rid, name, password, personnel_name, personnel_email, personnel_phone }
  const [shiftsDlg, setShiftsDlg] = useState(null); // { reg, rows, loading }
  const [policyDlg, setPolicyDlg] = useState(null); // { enabled, interval_days, notify_email }
  const [hFrom, setHFrom] = useState("");
  const [hTo, setHTo] = useState("");

  const load = () => {
    setLoading(true);
    return api.get("/registers")
      .then((r) => setRegs(r.data || []))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Kasalar yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/locations").then((r) => {
      let list = r.data || [];
      const myLocs = me?.location_ids?.length ? me.location_ids : (me?.location_id ? [me.location_id] : []);
      const isGlobal = me?.is_owner || me?.role === "admin" || me?.role === "assistant";
      if (!isGlobal && myLocs.length) list = list.filter((l) => myLocs.includes(l.id));
      setLocations(list);
    }).catch(() => {});
  }, [me]);

  // Seçili lokasyondaki atanabilir personeli getir (dialog açıkken)
  useEffect(() => {
    if (!open || !form.location_id) { setPersonnel([]); return; }
    api.get(`/registers/assignable-personnel?location_id=${form.location_id}`)
      .then((r) => setPersonnel(r.data || []))
      .catch(() => setPersonnel([]));
  }, [open, form.location_id]);

  const openNew = () => { setEditing(null); setRegenerate(false); setForm({ ...emptyForm, location_id: locations[0]?.id || "" }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r); setRegenerate(false);
    setForm({ name: r.name || "", location_id: r.location_id || "", active: r.active !== false, personnel_id: r.personnel_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Kasa adı zorunludur");
    if (!form.location_id) return toast.error("Lokasyon seçin");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), location_id: form.location_id, active: form.active, personnel_id: form.personnel_id || "" };
      if (editing && regenerate) payload.regenerate_password = true;
      const { data } = editing ? await api.put(`/registers/${editing.id}`, payload) : await api.post("/registers", payload);
      toast.success(editing ? "Kasa güncellendi" : "Kasa oluşturuldu");
      setOpen(false); load();
      if (data?.open_password) {
        setCreds({ rid: editing?.id || data.id, name: form.name.trim(), password: data.open_password, personnel_name: data.personnel_name || "", personnel_email: data.personnel_email || "", personnel_phone: data.personnel_phone || "" });
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); } finally { setBusy(false); }
  };

  const copyPw = () => { try { navigator.clipboard.writeText(creds.password); toast.success("Kasa şifresi kopyalandı"); } catch { /* noop */ } };

  const notifyEmail = async () => {
    if (!creds?.rid) return;
    try { await api.post(`/registers/${creds.rid}/notify-password`, {}); toast.success("Kasa şifresi personelin e-postasına gönderildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "E-posta gönderilemedi"); }
  };
  const waSend = () => {
    const phone = (creds?.personnel_phone || "").replace(/\D/g, "");
    const msg = `Groot ONE — Kasa şifreniz (${creds?.name}): ${creds?.password}. Bu 4 haneli şifreyle kasanızı açıp kapatabilirsiniz.`;
    const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };
  const openShifts = async (r) => {
    setShiftsDlg({ reg: r, rows: [], loading: true });
    try { const { data } = await api.get(`/registers/${r.id}/shifts?days=7`); setShiftsDlg({ reg: r, rows: data || [], loading: false }); }
    catch { setShiftsDlg({ reg: r, rows: [], loading: false }); }
  };

  const exportShifts = async (reg, format) => {
    try {
      const params = { format, days: 30 };
      if (hFrom) params.date_from = new Date(hFrom).toISOString();
      if (hTo) params.date_to = new Date(hTo + "T23:59:59").toISOString();
      const res = await api.get(`/registers/${reg.id}/shifts/export`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kasa-devir-${(reg.name || "kasa").replace(/\s+/g, "_")}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(format === "pdf" ? "PDF indirildi" : "Excel indirildi");
    } catch { toast.error("Dışa aktarılamadı"); }
  };

  const openPolicy = async () => {
    try { const { data } = await api.get("/registers/policy"); setPolicyDlg(data); }
    catch { setPolicyDlg({ enabled: false, interval_days: 30, notify_email: true }); }
  };
  const savePolicy = async () => {
    try { await api.post("/registers/policy", policyDlg); toast.success("Şifre politikası kaydedildi"); setPolicyDlg(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Kaydedilemedi"); }
  };
  const rotateNow = async () => {
    try {
      const { data } = await api.post("/registers/rotate-now");
      toast.success(`${data.rotated} kasa şifresi yenilendi${data.notified ? ` · ${data.notified} bildirim gönderildi` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const revealPw = async (r) => {
    try {
      const { data } = await api.get(`/registers/${r.id}/password`);
      setCreds({ rid: r.id, name: r.name, password: data.password, personnel_name: data.personnel_name, personnel_email: data.personnel_email || "", personnel_phone: data.personnel_phone || "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Şifre görüntülenemedi"); }
  };

  const toggleActive = async (r) => {
    try { await api.put(`/registers/${r.id}`, { active: r.active === false }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız"); }
  };

  const del = async (r) => {
    if (!window.confirm(`'${r.name}' kasasını silmek istediğinize emin misiniz?`)) return;
    try { await api.delete(`/registers/${r.id}`); toast.success("Kasa silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Silinemedi"); }
  };

  const shown = locFilter ? regs.filter((r) => r.location_id === locFilter) : regs;
  const activeCount = regs.filter((r) => r.active !== false).length;

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="registers-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-brand-green">
            <Calculator className="h-6 w-6" />
            <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Kasalar</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Her lokasyona istediğiniz kadar fiziksel kasa tanımlayın (Kasa 1, Kasa 2 …). Kasaya <b>bir personel atayın</b> — sistem
            otomatik bir <b>kasa şifresi</b> üretir. Bu şifreyi kasiyere verin; kasiyer bu şifreyle kasasını açar ve kapatır.
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button data-testid="registers-policy-btn" variant="outline" onClick={openPolicy} className="border-slate-300">
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Şifre Politikası
            </Button>
            <Button data-testid="registers-add-btn" onClick={openNew} className="bg-brand-orange text-white hover:bg-orange-600">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Kasa
            </Button>
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5">
          <div className="text-[11px] font-medium text-slate-400">Toplam Kasa</div>
          <div className="text-lg font-extrabold text-slate-800" data-testid="registers-total">{regs.length}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5">
          <div className="text-[11px] font-medium text-slate-400">Aktif</div>
          <div className="text-lg font-extrabold text-emerald-600" data-testid="registers-active">{activeCount}</div>
        </div>
        {locations.length > 1 && (
          <select data-testid="registers-loc-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
            className="ml-auto h-9 rounded-md border border-input bg-white px-2 text-sm">
            <option value="">Tüm Lokasyonlar</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
      ) : shown.length === 0 ? (
        <div data-testid="registers-empty" className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 py-16 text-center">
          <Calculator className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-500">Henüz kasa tanımlanmadı.</p>
          {canManage && <p className="mt-1 text-xs text-slate-400">"Yeni Kasa" ile bu lokasyon için ilk kasanızı ekleyin.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((r) => (
            <div key={r.id} data-testid={`register-card-${r.id}`}
              className={`rounded-2xl border bg-white p-4 shadow-sm transition-all hover:shadow ${r.active === false ? "border-slate-200 opacity-70" : "border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {r.personnel_name
                      ? <span data-testid={`register-personnel-${r.id}`} className="inline-flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-0.5 text-[11px] font-bold text-brand-green"><User className="h-3 w-3" /> {r.personnel_name}</span>
                      : <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><User className="h-3 w-3" /> Personel atanmadı</span>}
                    {r.has_password && <span title="Kasa şifresi tanımlı" className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500"><KeyRound className="h-3 w-3" /></span>}
                    {r.active === false && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">Pasif</span>}
                  </div>
                  <h3 className="mt-1.5 font-heading text-lg font-bold text-slate-800">{r.name}</h3>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400"><MapPin className="h-3 w-3" /> {r.location_name || "—"}</div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button data-testid={`register-shifts-${r.id}`} onClick={() => openShifts(r)} title="Kasa devir geçmişi (kim açtı/kapattı)"
                      className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-sky-50 hover:text-sky-600"><History className="h-4 w-4" /></button>
                    {r.has_password && (
                      <button data-testid={`register-reveal-${r.id}`} onClick={() => revealPw(r)} title="Kasa şifresini gör (personel unutursa)"
                        className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-600"><KeyRound className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`register-toggle-${r.id}`} onClick={() => toggleActive(r)} title={r.active === false ? "Aktifleştir" : "Pasifleştir"}
                      className={`rounded-md p-1.5 transition-colors ${r.active === false ? "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600" : "text-emerald-600 hover:bg-emerald-50"}`}><Power className="h-4 w-4" /></button>
                    <button data-testid={`register-edit-${r.id}`} onClick={() => openEdit(r)} className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-brand-green"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`register-delete-${r.id}`} onClick={() => del(r)} className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="register-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Kasayı Düzenle" : "Yeni Kasa"}</DialogTitle>
            <DialogDescription>Kasaya bir personel atayın; sistem otomatik bir kasa şifresi üretir (veya kendiniz belirleyin). Bu şifreyi kasiyere verin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Kasa Adı</Label>
              <Input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn. 1. Kasa" className="mt-1" />
            </div>
            <div>
              <Label>Lokasyon</Label>
              <select data-testid="register-location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value, personnel_id: "" })}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Seçin…</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> Kasaya Atanan Personel</Label>
              <select data-testid="register-personnel" value={form.personnel_id} onChange={(e) => setForm({ ...form, personnel_id: e.target.value })}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-2 text-sm" disabled={!form.location_id}>
                <option value="">— Personel seçin —</option>
                {personnel.map((p) => <option key={p.id} value={p.id}>{p.name}{p.username ? ` (${p.username})` : ""}</option>)}
              </select>
              {!form.location_id && <p className="mt-1 text-[11px] text-slate-400">Önce lokasyon seçin.</p>}
              {form.location_id && personnel.length === 0 && <p className="mt-1 text-[11px] text-amber-600">Bu lokasyonda atanabilir personel yok — önce Kullanıcılar'dan ekleyin.</p>}
            </div>
            {form.personnel_id && (
              <div>
                <Label className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Kasa Şifresi <span className="text-slate-400">(4 haneli)</span></Label>
                <Input data-testid="register-password" value={form.open_password} onChange={(e) => setForm({ ...form, open_password: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  inputMode="numeric" maxLength={4}
                  placeholder={editing ? "4 haneli · boş → korunur" : "4 haneli · boş → otomatik üretilir"} className="mt-1 font-mono text-lg tracking-widest" />
                <p className="mt-1 text-[11px] text-slate-400">Yalnızca 4 rakam. Kasiyer bu şifreyle kasasını açar ve kapatır. Boş bırakırsanız sistem 4 haneli bir şifre üretir.</p>
                {editing && (
                  <label className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-600">
                    <input type="checkbox" data-testid="register-regenerate" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                    <RefreshCw className="h-3.5 w-3.5" /> Yeni otomatik şifre üret
                  </label>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="register-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
              Aktif kasa
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button data-testid="register-save" disabled={busy} onClick={save} className="bg-brand-orange text-white hover:bg-orange-600">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {editing ? "Kaydet" : "Oluştur"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creds} onOpenChange={(o) => { if (!o) setCreds(null); }}>
        <DialogContent data-testid="register-creds-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><KeyRound className="h-5 w-5 text-brand-orange" /> Kasa Şifresi</DialogTitle>
            <DialogDescription>Bu şifreyi <b>{creds?.personnel_name || "kasiyere"}</b> verin. Kasiyer bu şifreyle <b>{creds?.name}</b> kasasını açar ve kapatır.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border-2 border-dashed border-brand-orange/40 bg-orange-50/60 p-4 text-center">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Kasa Şifresi</div>
            <div data-testid="register-creds-password" className="mt-1 select-all font-mono text-2xl font-extrabold tracking-widest text-slate-900">{creds?.password}</div>
          </div>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            {creds?.personnel_email && (
              <Button data-testid="register-creds-email" variant="outline" onClick={notifyEmail}><Mail className="mr-1.5 h-4 w-4" /> E-posta Gönder</Button>
            )}
            <Button data-testid="register-creds-wa" variant="outline" onClick={waSend} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp</Button>
            <Button data-testid="register-creds-copy" variant="outline" onClick={copyPw}><Copy className="mr-1.5 h-4 w-4" /> Kopyala</Button>
            <Button data-testid="register-creds-close" onClick={() => setCreds(null)} className="bg-brand-green text-white hover:bg-brand-green-600">Tamam</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shiftsDlg} onOpenChange={(o) => { if (!o) setShiftsDlg(null); }}>
        <DialogContent data-testid="register-shifts-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><History className="h-5 w-5 text-sky-600" /> Kasa Devir Geçmişi</DialogTitle>
            <DialogDescription>{shiftsDlg?.reg?.name} — son 7 günde kim açtı / kapattı.</DialogDescription>
          </DialogHeader>
          {shiftsDlg?.loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>
          ) : (shiftsDlg?.rows || []).length === 0 ? (
            <div data-testid="register-shifts-empty" className="py-8 text-center text-sm text-slate-400">Bu kasada son 7 günde vardiya yok.</div>
          ) : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {shiftsDlg.rows.map((s, i) => (
                <div key={s.shift_id} data-testid={`register-shift-row-${i}`} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold text-slate-800">{s.cashier_name}</div>
                    <div className="text-[11px] text-slate-400">{fmtDT(s.opened_at)} → {s.closed_at ? fmtDT(s.closed_at) : "açık"}</div>
                  </div>
                  <div className="text-right">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${s.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{s.status === "open" ? "AÇIK" : "KAPALI"}</span>
                    {s.grand_total != null && <div className="mt-0.5 tabular text-xs font-semibold text-slate-700">{Number(s.grand_total).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {curSym()}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-xs">
              <input type="date" data-testid="register-export-from" value={hFrom} onChange={(e) => setHFrom(e.target.value)} className="h-8 rounded-md border border-input px-2" />
              <span className="text-slate-400">→</span>
              <input type="date" data-testid="register-export-to" value={hTo} onChange={(e) => setHTo(e.target.value)} className="h-8 rounded-md border border-input px-2" />
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="register-export-xlsx" variant="outline" size="sm" onClick={() => exportShifts(shiftsDlg.reg, "xlsx")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
              <Button data-testid="register-export-pdf" variant="outline" size="sm" onClick={() => exportShifts(shiftsDlg.reg, "pdf")} className="border-rose-300 text-rose-700 hover:bg-rose-50"><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
              <Button data-testid="register-shifts-close" onClick={() => setShiftsDlg(null)} variant="outline" size="sm">Kapat</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!policyDlg} onOpenChange={(o) => { if (!o) setPolicyDlg(null); }}>
        <DialogContent data-testid="register-policy-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><ShieldCheck className="h-5 w-5 text-brand-green" /> Kasa Şifre Politikası</DialogTitle>
            <DialogDescription>Kasa şifrelerini belirli aralıkta otomatik yenile ve yeni şifreyi atanan personele e-posta ile bildir.</DialogDescription>
          </DialogHeader>
          {policyDlg && (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" data-testid="policy-enabled" checked={!!policyDlg.enabled} onChange={(e) => setPolicyDlg({ ...policyDlg, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                Otomatik yenilemeyi aç
              </label>
              <div>
                <Label>Yenileme aralığı (gün)</Label>
                <Input data-testid="policy-interval" type="number" min="1" value={policyDlg.interval_days} onChange={(e) => setPolicyDlg({ ...policyDlg, interval_days: parseInt(e.target.value) || 30 })} className="mt-1 w-32" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" data-testid="policy-notify" checked={!!policyDlg.notify_email} onChange={(e) => setPolicyDlg({ ...policyDlg, notify_email: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                Yeni şifreyi personele e-posta ile bildir
              </label>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" data-testid="policy-report-email" checked={!!policyDlg.report_email_enabled} onChange={(e) => setPolicyDlg({ ...policyDlg, report_email_enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                Günlük kasa devir raporunu müdür ve üstlerine e-posta gönder
              </label>
              <div className="rounded-md bg-slate-50 p-3 text-[11px] text-slate-500 ring-1 ring-inset ring-slate-200">
                Günlük otomatik kontrol çalışır; süresi dolan kasaların şifresi yenilenir. Aşağıdaki butonla şimdi de tetikleyebilirsiniz.
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap justify-between gap-2">
            <Button data-testid="policy-rotate-now" variant="outline" onClick={rotateNow} className="border-brand-orange text-brand-orange hover:bg-orange-50"><RefreshCw className="mr-1.5 h-4 w-4" /> Süresi Dolanları Şimdi Yenile</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPolicyDlg(null)}>Vazgeç</Button>
              <Button data-testid="policy-save" onClick={savePolicy} className="bg-brand-green text-white hover:bg-brand-green-600">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
